import { TextField as AriaTextField, type TextFieldProps as AriaTextFieldProps, Input } from "react-aria-components";
import { cx } from "@/lib/cx";
import { type BaseFieldProps, FieldDescription, FieldErrorText, FieldLabel, fieldRoot, inputBase } from "./field";

export interface CodeFieldProps extends Omit<AriaTextFieldProps, "children" | "className">, BaseFieldProps {
  className?: string;
  /** "totp": six digits from an authenticator. "backup": a recovery code (letters, digits, dash). */
  kind?: "totp" | "backup";
}

/**
 * One-time code input for sign-in and enrolment: large, centred, mono with wide tracking, numeric
 * keypad and one-time-code autofill for authenticator codes. Spaces are stripped as you type.
 */
export function CodeField({
  label,
  labelHidden,
  description,
  error,
  kind = "totp",
  className,
  onChange,
  ...props
}: CodeFieldProps) {
  const totp = kind === "totp";
  return (
    <AriaTextField
      className={cx(fieldRoot, className)}
      isInvalid={error ? true : props.isInvalid}
      inputMode={totp ? "numeric" : "text"}
      autoComplete={totp ? "one-time-code" : "off"}
      maxLength={totp ? 6 : 24}
      onChange={(v) => onChange?.(totp ? v.replace(/\D/g, "").slice(0, 6) : v.replace(/\s/g, ""))}
      {...props}
    >
      <FieldLabel hidden={labelHidden}>{label}</FieldLabel>
      <Input
        spellCheck={false}
        autoCapitalize="off"
        className={cx(
          inputBase,
          "h-11 text-center font-mono text-lg font-medium",
          totp ? "tracking-[0.5em] indent-[0.5em]" : "tracking-[0.12em]",
        )}
      />
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldErrorText>{error}</FieldErrorText>
    </AriaTextField>
  );
}
